import { createSelector } from 'reselect';

import { LoadStatus, State } from '../../Types';
import { BusinessState } from './Types';
import { RoleId } from './Types/Role';

type OccupantId = number | string;

const businessState = ({ business }: State) => business;

/* Occupants */
const occupantsSlice = createSelector(businessState, ({ occupants }: BusinessState) => occupants);

export const occupantsLoadStatus = createSelector(occupantsSlice, ({ loadStatus }) => loadStatus);

export const occupantsAreLoaded = createSelector(
    occupantsLoadStatus,
    (loadStatus: LoadStatus) => loadStatus === LoadStatus.LOADED,
);

export const occupants = createSelector(occupantsSlice, ({ loadStatus, sortOrder, ...occupants }) => occupants);

export const sortedOccupantsList = createSelector(
    occupantsSlice,
    ({ loadStatus, sortOrder, ...occupants }) => sortOrder?.map((id) => occupants[id]) || [],
);

export const occupant = (occupantId: number | string) =>
    createSelector(occupantsSlice, (occupants) => (occupants?.hasOwnProperty(occupantId) ? occupants[occupantId] : {}));

export const occupantCount = createSelector(occupantsSlice, ({ sortOrder = [] }) => sortOrder.length);

export const occupantMailingAddress = (occupantId: number | string) =>
    createSelector(occupant(occupantId), ({ mailingAddress }) => mailingAddress);

export const occupantIsFullyLoaded = (occupantId: OccupantId) =>
    createSelector(occupant(occupantId), ({ loadStatus }) => loadStatus === LoadStatus.LOADED);

/* Business Users */
const businessUsersSlice = createSelector(businessState, ({ businessUsers }: BusinessState) => businessUsers);

export const businessUsersOccupantLoadStatus = (occupantId: OccupantId) =>
    createSelector(businessUsersSlice, (businessUsers) =>
        businessUsers.hasOwnProperty(occupantId) ? businessUsers[occupantId].loadStatus : LoadStatus.INITIAL_STATE,
    );

export const sortedBusinessUsersForOccupant = (occupantId: OccupantId) =>
    createSelector(businessUsersSlice, (businessUsers) => {
        if (businessUsers.hasOwnProperty(occupantId)) {
            const { loadStatus = LoadStatus.INITIAL_STATE, sortOrder = [], ...rest } = businessUsers[occupantId];

            return sortOrder.map((i: number) => rest[i]);
        }

        return [];
    });

/* Roles */
const rolesSlice = createSelector(businessState, ({ roles }: BusinessState) => roles);

export const occupantRoles = (occupantId: OccupantId) =>
    createSelector(rolesSlice, (rolesByOccupant) => {
        if (Object.hasOwn(rolesByOccupant, occupantId)) {
            const { loadStatus, sortOrder, errorState, ...rolesById } = rolesByOccupant[occupantId];

            return rolesById;
        }

        return {};
    });

export const occupantRolesLoadStatus = (occupantId: OccupantId) =>
    createSelector(rolesSlice, (rolesByOccupant) => {
        if (Object.hasOwn(rolesByOccupant, occupantId)) {
            return rolesByOccupant[occupantId].loadStatus;
        }

        return LoadStatus.INITIAL_STATE;
    });

export const occupantSortedRoleList = (occupantId: OccupantId) =>
    createSelector(rolesSlice, (rolesByOccupant) => {
        if (Object.hasOwn(rolesByOccupant, occupantId)) {
            const { loadStatus, sortOrder, errorState, ...rolesById } = rolesByOccupant[occupantId];

            return sortOrder?.map((roleId: RoleId) => rolesById[roleId]) || [];
        }

        return [];
    });